import { BookOpenIcon, ClockIcon, FileTextIcon, TrendUpIcon, WarningCircle } from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";

import { StatCard } from "../../../components/adminDashboard/AICompanion/StatCard";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { CourseInsightsSentimentLabel, useGetCourseModuleInsightsQuery } from "@axio/graphql/generated/graphql";
import { colors, radii, shadowStyles, spacing, webTransitions } from "../../../styles/design-system";
import { formatPercentage } from "../../../utils/analyticsUtils";
import { formatDurationFromMinutes } from "../../../utils/timeUtils";
import ModuleInsightsCard from "./components/ModuleInsightsCard";

interface AdminCourseModulesContainerProps {
  courseId: string;
}

const objectiveCardColors = {
  borderColor: colors.error[300],
  hoverBackgroundColor: colors.error[25],
  hoverTextColor: colors.error[600],
  hoverMetaColor: colors.error[500],
} as const;

const topicCardColors = {
  borderColor: colors.accent.purple200,
  hoverBorderColor: "#C4B5FD",
  hoverBackgroundColor: colors.purple[25],
  hoverTextColor: colors.accent.purple500,
} as const;

const moduleReviewCardColors = {
  borderColor: colors.warning[400],
  hoverBackgroundColor: colors.warning[25],
  hoverTextColor: colors.warning[700],
  hoverMetaColor: colors.warning[600],
} as const;

const AdminCourseModulesContainer: React.FC<AdminCourseModulesContainerProps> = ({ courseId }) => {
  const [showAllConcepts, setShowAllConcepts] = useState(false);
  const [hoveredObjectiveId, setHoveredObjectiveId] = useState<string | null>(null);
  const [hoveredTopicId, setHoveredTopicId] = useState<string | null>(null);
  const [hoveredModuleId, setHoveredModuleId] = useState<string | null>(null);
  const handleAttentionItemPress = () => undefined;

  const { data, loading, error } = useGetCourseModuleInsightsQuery({
    variables: { courseId },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
  });

  const payload = data?.getCourseModuleInsights?.data;
  const summaryCards = payload?.summaryCards;
  const moduleCards = payload?.moduleCards ?? [];
  const objectivesNeedingIntervention = payload?.objectivesNeedingIntervention ?? [];
  const courseConfusionTopics = payload?.courseConfusionTopics ?? [];

  const flaggedObjectives = useMemo(
    () => objectivesNeedingIntervention.filter((objective) => objective.confusionPoints >= 3),
    [objectivesNeedingIntervention]
  );

  const totalConfusionPoints = useMemo(
    () => courseConfusionTopics.reduce((sum, topic) => sum + topic.count, 0),
    [courseConfusionTopics]
  );

  const modulesNeedingReview = useMemo(
    () =>
      moduleCards
        .filter(
          (moduleCard) =>
            (moduleCard.aggregateGradePct ?? 100) < 75 ||
            moduleCard.completionPct < 70 ||
            moduleCard.sentiment === CourseInsightsSentimentLabel.Negative
        )
        .sort((a, b) => {
          const aScore = a.aggregateGradePct ?? 100;
          const bScore = b.aggregateGradePct ?? 100;
          if (aScore !== bScore) return aScore - bScore;
          return a.completionPct - b.completionPct;
        })
        .slice(0, 3),
    [moduleCards]
  );

  const summaryStatCards = [
    {
      icon: <BookOpenIcon size={18} color={colors.brand[600]} weight="bold" />,
      iconBackgroundColor: "#D6EEFF",
      value: `${summaryCards?.totalModules ?? 0}`,
      label: "Total Modules",
      color: "blue" as const,
    },
    {
      icon: <FileTextIcon size={18} color={colors.accent.purple500} weight="bold" />,
      iconBackgroundColor: colors.accent.purple100,
      value: `${summaryCards?.totalActivities ?? 0}`,
      label: "Total Activities",
      color: "purple" as const,
    },
    {
      icon: <TrendUpIcon size={18} color={colors.success[700]} weight="bold" />,
      iconBackgroundColor: colors.success[100],
      value: formatPercentage(summaryCards?.avgCompletionPct ?? 0),
      label: "Avg Completion",
      color: "green" as const,
    },
    {
      icon: <ClockIcon size={18} color={colors.warning[700]} weight="bold" />,
      iconBackgroundColor: colors.warning[50],
      value: formatDurationFromMinutes(summaryCards?.estimatedTotalTimeMinutes ?? 0),
      label: "Est. Total Time",
      color: "orange" as const,
    },
  ];

  if (loading && !payload) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={colors.brand[500]} />
      </View>
    );
  }

  if (error || !payload || !summaryCards) {
    return (
      <View style={styles.errorContainer}>
        <CustomText
          text={error?.message || "Failed to load module insights"}
          size="m"
          weight="medium"
          useTranslationText={false}
        />
      </View>
    );
  }

  return (
    <View style={[styles.scroll, styles.content]}>
      <View style={styles.pageHeading}>
        <CustomText text="Course Modules" size="m" weight="bold" useTranslationText={false} />
        <CustomText
          text="View course structure and student performance by module"
          size="s"
          useTranslationText={false}
          style={styles.mutedText}
        />
      </View>

      <View style={styles.summaryGrid}>
        {summaryStatCards.map((card) => (
          <StatCard
            key={card.label}
            variant="summary"
            icon={card.icon}
            iconBackgroundColor={card.iconBackgroundColor}
            value={card.value}
            label={card.label}
            color={card.color}
          />
        ))}
      </View>

      <View style={[styles.attentionCard, shadowStyles.xs]}>
        <View style={styles.attentionHeader}>
          <View style={styles.attentionTitleRow}>
            <WarningCircle size={24} color={colors.grey[950]} />
            <CustomText text="Needs Your Attention" size="xl" weight="bold" useTranslationText={false} />
          </View>
          <CustomText
            text={`${flaggedObjectives.length} flagged objectives • ${totalConfusionPoints} confusion points`}
            size="s"
            useTranslationText={false}
            style={styles.mutedText}
          />
        </View>

        <View style={styles.attentionColumns}>
          <View style={styles.attentionColumn}>
            <CustomText text="Objectives Needing Intervention" size="s" weight="bold" useTranslationText={false} />
            {flaggedObjectives.length === 0 ? (
              <CustomText
                text="No objectives currently flagged."
                size="s"
                useTranslationText={false}
                style={styles.mutedText}
              />
            ) : (
              flaggedObjectives.slice(0, 5).map((objective) => {
                const isHovered = hoveredObjectiveId === objective.objectiveId;

                return (
                  <Pressable
                    key={objective.objectiveId}
                    accessibilityRole="button"
                    onPress={handleAttentionItemPress}
                    onHoverIn={() => setHoveredObjectiveId(objective.objectiveId)}
                    onHoverOut={() => setHoveredObjectiveId(null)}
                    style={[
                      styles.attentionListItem,
                      styles.attentionPressableItem,
                      styles.objectiveItem,
                      {
                        borderColor: isHovered ? objectiveCardColors.borderColor : colors.grey[200],
                        borderLeftColor: objectiveCardColors.borderColor,
                      },
                      isHovered && { backgroundColor: objectiveCardColors.hoverBackgroundColor },
                    ]}>
                    <View style={styles.attentionListItemText}>
                      <CustomText
                        text={objective.objectiveLabel}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={isHovered && { color: objectiveCardColors.hoverTextColor }}
                      />
                      <CustomText
                        text={`${objective.activityTitle} • ${objective.moduleTitle}`}
                        size="xs"
                        useTranslationText={false}
                        style={[styles.mutedText, isHovered && { color: objectiveCardColors.hoverMetaColor }]}
                      />
                    </View>
                    <View style={styles.objectiveCountBadge}>
                      <CustomText
                        text={`${objective.confusionPoints}`}
                        size="xs"
                        weight="bold"
                        useTranslationText={false}
                        style={styles.objectiveCountText}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
          </View>

          <View style={styles.attentionColumn}>
            <CustomText text="Common Confusion Points" size="s" weight="bold" useTranslationText={false} />
            {courseConfusionTopics.length === 0 ? (
              <CustomText
                text="No confusion points detected."
                size="s"
                useTranslationText={false}
                style={styles.mutedText}
              />
            ) : (
              (showAllConcepts ? courseConfusionTopics : courseConfusionTopics.slice(0, 3)).map((topic) => {
                const isHovered = hoveredTopicId === topic.topicId;

                return (
                  <Pressable
                    key={topic.topicId}
                    accessibilityRole="button"
                    onPress={handleAttentionItemPress}
                    onHoverIn={() => setHoveredTopicId(topic.topicId)}
                    onHoverOut={() => setHoveredTopicId(null)}
                    style={[
                      styles.attentionListItem,
                      styles.attentionPressableItem,
                      styles.conceptItem,
                      {
                        borderColor: isHovered ? topicCardColors.hoverBorderColor : colors.grey[200],
                        borderLeftColor: isHovered ? topicCardColors.hoverBorderColor : topicCardColors.borderColor,
                      },
                      isHovered && { backgroundColor: topicCardColors.hoverBackgroundColor },
                    ]}>
                    <View style={styles.attentionListItemText}>
                      <CustomText
                        text={topic.summary || topic.title}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={isHovered && { color: topicCardColors.hoverTextColor }}
                      />
                      <CustomText
                        text={topic.moduleTitle ? `in ${topic.moduleTitle}` : "Course-wide"}
                        size="xs"
                        useTranslationText={false}
                        style={[styles.mutedText, isHovered && { color: topicCardColors.hoverTextColor }]}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
            {courseConfusionTopics.length > 3 ? (
              <Pressable style={styles.viewAllConceptsButton} onPress={() => setShowAllConcepts((prev) => !prev)}>
                <CustomText
                  text={showAllConcepts ? "← Show less" : `View all ${courseConfusionTopics.length} concepts →`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.inlineLinkText}
                />
              </Pressable>
            ) : null}
          </View>

          <View style={styles.attentionColumn}>
            <CustomText text="Modules Needing Review" size="s" weight="bold" useTranslationText={false} />
            {modulesNeedingReview.length === 0 ? (
              <CustomText
                text="No modules currently flagged."
                size="s"
                useTranslationText={false}
                style={styles.mutedText}
              />
            ) : (
              modulesNeedingReview.map((moduleCard) => {
                const isHovered = hoveredModuleId === moduleCard.moduleId;

                return (
                  <Pressable
                    key={moduleCard.moduleId}
                    accessibilityRole="button"
                    onPress={handleAttentionItemPress}
                    onHoverIn={() => setHoveredModuleId(moduleCard.moduleId)}
                    onHoverOut={() => setHoveredModuleId(null)}
                    style={[
                      styles.attentionListItem,
                      styles.attentionPressableItem,
                      styles.moduleReviewItem,
                      {
                        borderColor: isHovered ? moduleReviewCardColors.borderColor : colors.grey[200],
                        borderLeftColor: moduleReviewCardColors.borderColor,
                      },
                      isHovered && { backgroundColor: moduleReviewCardColors.hoverBackgroundColor },
                    ]}>
                    <View style={styles.attentionListItemText}>
                      <CustomText
                        text={moduleCard.moduleTitle}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={isHovered && { color: moduleReviewCardColors.hoverTextColor }}
                      />
                      <CustomText
                        text={`${formatPercentage(moduleCard.completionPct)} completion`}
                        size="xs"
                        useTranslationText={false}
                        style={[styles.mutedText, isHovered && { color: moduleReviewCardColors.hoverMetaColor }]}
                      />
                    </View>
                    <View style={styles.moduleScoreBadge}>
                      <CustomText
                        text={
                          typeof moduleCard.aggregateGradePct === "number"
                            ? formatPercentage(moduleCard.aggregateGradePct)
                            : "—"
                        }
                        size="xs"
                        weight="bold"
                        useTranslationText={false}
                        style={styles.moduleScoreText}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
          </View>
        </View>
      </View>

      <View style={styles.modulesSection}>
        {moduleCards.map((module, index) => (
          <ModuleInsightsCard
            key={module.moduleId}
            moduleCard={module}
            moduleIndex={index + 1}
            activities={module.activities}
            defaultExpanded={index === 0}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: "transparent",
  },
  content: {
    padding: spacing.lg,
    gap: spacing.lg,
  },
  loaderContainer: {
    minHeight: 240,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    minHeight: 180,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    margin: spacing.lg,
    padding: spacing.lg,
  },
  pageHeading: {
    gap: 4,
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  attentionCard: {
    backgroundColor: "rgba(255,255,255,0.9)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.xl,
    overflow: "hidden",
  },
  attentionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  attentionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  attentionColumns: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    padding: spacing.md,
  },
  attentionColumn: {
    flex: 1,
    minWidth: 240,
    gap: spacing.xs,
  },
  attentionListItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    backgroundColor: colors.white,
    ...webTransitions.fast,
  },
  attentionPressableItem: {
    // @ts-expect-error web-only property
    cursor: "pointer",
  },
  objectiveItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.error[300],
  },
  conceptItem: {
    borderLeftWidth: 2,
    borderLeftColor: topicCardColors.borderColor,
  },
  moduleReviewItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.warning[400],
  },
  attentionListItemText: {
    flex: 1,
    gap: 2,
  },
  objectiveCountBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.error[50],
    borderWidth: 1,
    borderColor: colors.error[200],
  },
  objectiveCountText: {
    color: colors.error[700],
  },
  moduleScoreBadge: {
    minWidth: 42,
    height: 26,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.warning[50],
    borderWidth: 1,
    borderColor: colors.warning[100],
    paddingHorizontal: spacing.xs,
  },
  moduleScoreText: {
    color: colors.warning[700],
  },
  mutedText: {
    color: colors.grey[500],
  },
  viewAllConceptsButton: {
    marginTop: spacing.xs,
    alignSelf: "center",
  },
  inlineLinkText: {
    color: colors.grey[500],
  },
  modulesSection: {
    gap: spacing.md,
  },
});

export default AdminCourseModulesContainer;
